/* eslint-disable max-classes-per-file */
import React from "react";
import {
    DndProvider, DragLayer,
    DragSource,
    DropTarget,
} from "react-dnd";
import {getEmptyImage, HTML5Backend} from "react-dnd-html5-backend";
import PropTypes from "prop-types";
import "./DragDrop.less";

const DIRECTION = {
    UPWARD: "upward",
    DOWNWARD: "downward",
}


class Container extends React.Component {
    render() {
        const {children} = this.props;
        return (
            <DndProvider backend={HTML5Backend}>
                {children}
            </DndProvider>
        );
    }
}

const dragSpec = {
    beginDrag(props) {
        props.beginDrag?.();

        return {
            index: props.index,
            group: props.group,
        };
    },

    endDrag(props) {
        props.endDrag?.();
    }
};

const dropSpec = {
    hover(props, monitor, component) {
        const {index: dragIndex} = monitor.getItem();
        const {index: overIndex} = props;
        if (!monitor.canDrop()) {
            return component.setState({
                direction: null,
            })
        }

        const clientOffset = monitor.getClientOffset();
        const clientRect = component.sourceRef.getBoundingClientRect();
        const clientHeight = clientRect.height;
        const offsetY = clientOffset.y - clientRect.top;

        if (offsetY < clientHeight / 2) {
            component.setState({
                direction: DIRECTION.UPWARD,
            });
        } else {
            component.setState({
                direction: DIRECTION.DOWNWARD,
            });
        }
        props.move?.(dragIndex, overIndex);
    },

    drop(props, monitor, component) {
        const {index: removeIndex} = monitor.getItem();
        let {index: insertIndex} = props;

        if (removeIndex === insertIndex) return;

        const direction = component.state.direction;
        if (removeIndex < insertIndex) {
            if (direction === DIRECTION.UPWARD) {
                insertIndex -= 1;
            }
        } else if (removeIndex > insertIndex) {
            if (direction === DIRECTION.DOWNWARD) {
                insertIndex += 1;
            }
        }

        if (removeIndex !== insertIndex) {
            props.drop?.(removeIndex, insertIndex);
        }
    },

    canDrop(props, monitor) {
        const {index: dragIndex, group: dragGroup} = monitor.getItem();
        const {index: hoverIndex, group: hoverGroup} = props;

        return dragIndex !== hoverIndex && dragGroup === hoverGroup;
    }
};

const collect = (monitor) => {
    return {
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        currentOffset: monitor.getClientOffset(),
        isDragging: monitor.isDragging(),
    }
}

const DragPreviewLayer = DragLayer(collect)(({
    item,
    itemType,
    isDragging,
    currentOffset,
    renderPreview,
    index,
    group,
}) => {

    const layerStyles = {
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%'
    }

    function getItemStyles(props) {
        const { currentOffset } = props;
        if (!currentOffset) {
            return {
                display: "none"
            }
        }

        const { x, y } = currentOffset;
        const transform = `translate(${x}px, ${y}px)`;
        return {
            transform: transform,
            WebkitTransform: transform
        }
    }

    if (!isDragging || index !== item?.index || group !== item?.group) {

        return null;

    }

    return (
        <div style={layerStyles}>
            <div style={getItemStyles({currentOffset})}>{renderPreview()}</div>
        </div>
    )

})


@DragSource("card", dragSpec, (conn, monitor) => ({
    connectDragSource: conn.dragSource(),
    connectDragPreview: conn.dragPreview(),
}))
@DropTarget("card", dropSpec, (conn, monitor) => ({
    connectDropTarget: conn.dropTarget(),
    isOver: monitor.isOver(),
}))
class Item extends React.Component {

    static propTypes = {
        index: PropTypes.number.isRequired,
        move: PropTypes.func,
        renderPreview: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            direction: null,
        }
    }

    componentDidMount() {
        const {connectDragPreview} = this.props;
        connectDragPreview(getEmptyImage());
    }

    render() {
        const {
            index, group, move, children, moveIndicatorStyle, style,
            connectDragSource, connectDragPreview, connectDropTarget,
            renderPreview, dragItem, isOver, onClick,
            ...restProps
        } = this.props;

        let className = "";
        if (isOver) {
            if (this.state.direction === DIRECTION.DOWNWARD) {
                className = "drop-over-downward";
            } else if (this.state.direction === DIRECTION.UPWARD) {
                className = "drop-over-upward";
            }
        }
        
        return connectDropTarget(
            <div style={{position: "relative"}} onClick={onClick}>
                <DragPreviewLayer renderPreview={renderPreview} index={index} group={group} />
                {
                    connectDragSource(
                        <div className={className} ref={ref => this.sourceRef = ref} style={{cursor: "move", ...style}}>
                            {children}
                        </div>
                    )
                }
            </div>
        );
    }
}


export default {
    Container,
    Item,
};
